import { useState, useEffect, useRef } from "react";

const ProductParametersPanel = ({
    panelTitle,
    productKNumber,
    productCode,
    fdaLetterUrl,
    fields = [], // array of { name, label, value, type?, placeholder?, editable?, height? }
    onFieldChange, // function (name, value) => void
    addYesMaybeNoButtons = false,
    handleYes,
    handleMaybe,
    handleNo,
    isCandidate
}) => {

    const handleChange = (name, value) => {
        if (typeof onFieldChange === "function") {
            onFieldChange(name, value);
        }
    };

    // refs to the rendered read-only elements so we can detect truncation
    const elRefs = useRef({});
    const setRef = (name) => (el) => {
        if (el) elRefs.current[name] = el;
    };

    // map of fieldName -> boolean (is truncated)
    const [isTruncatedMap, setIsTruncatedMap] = useState({});

    // popup state to show full content when truncated item clicked
    const [popup, setPopup] = useState({ open: false, label: "", content: "" });

    // detect truncation after render and on window resize
    useEffect(() => {
        const updateTruncation = () => {
            const next = {};
            fields.forEach((f) => {
                const el = elRefs.current[f.name];
                if (!el) {
                    next[f.name] = false;
                    return;
                }
                // consider truncated if scrollHeight > clientHeight
                next[f.name] = el.scrollHeight > el.clientHeight + 1;
            });
            setIsTruncatedMap((prev) => {
                // avoid unnecessary state updates
                const same =
                    Object.keys(next).length === Object.keys(prev).length &&
                    Object.keys(next).every((k) => prev[k] === next[k]);
                return same ? prev : next;
            });
        };

        updateTruncation();
        window.addEventListener("resize", updateTruncation);
        return () => window.removeEventListener("resize", updateTruncation);
    }, [fields]);

    const openPopup = (label, content) => setPopup({ open: true, label, content });
    const closePopup = () => setPopup({ open: false, label: "", content: "" });

    // local state to control button appearance and force redraw on click
    const [localCandidate, setLocalCandidate] = useState(isCandidate);
    useEffect(() => {
        // If isCandidate is not 'Yes', 'Maybe', or 'No', log a message to the console
        if (isCandidate && !["Yes", "Maybe", "No"].includes(isCandidate)) {
            console.log("Unexpected isCandidate value:", isCandidate);
        }
        setLocalCandidate(isCandidate);
    }, [isCandidate]);

    if (!fdaLetterUrl || fdaLetterUrl.trim() === "") {
        // Split K-number into parts to construct URL, KXXYYYY
        const firstTwoNumbers = productKNumber.slice(1, 3);
        const nextFourNumbers = productKNumber.slice(3, 7);

        // Convert firstTwoNumbers to a number and check if less than 2 or greater than 50
        if (Number(firstTwoNumbers) < 2) {
            // Hack for years that don't have links
            fdaLetterUrl = `https://www.accessdata.fda.gov/cdrh_docs/pdf/K${firstTwoNumbers}${nextFourNumbers}.pdf`;
        } else if (Number(firstTwoNumbers) > 50) {
            // Hack for years that don't have links
            fdaLetterUrl = `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=${productKNumber}`;
        } else {
            // Pad firstTwoNumbers with leading zero if necessary
            const subfolder = firstTwoNumbers.padStart(2, '0');
            fdaLetterUrl = `https://www.accessdata.fda.gov/cdrh_docs/pdf${subfolder}/K${firstTwoNumbers}${nextFourNumbers}.pdf`;
        }
    }

    return (
        <div className="search-parameters-panel">
            <div className="feature-content-heading">{panelTitle}</div>

            {/* Add k-number from fields here with small arrow icons pointing left and right */}
            {/* <div className="k-number-nav-container"> */}
                {/* <span className="arrow left-arrow">←</span> */}
                {/* <span className="arrow left-arrow"></span> */}
                {/* <span className="k-number">{productKNumber}</span> */}
                {/* <span className="arrow right-arrow">→</span> */}
                {/* <span className="arrow right-arrow"></span> */}
            {/* </div> */}

            {/* Add deviceName from fields here */}
            <div className="prod-param-device-name">{fields.find(f => f.name === "deviceName")?.value}</div>

            {/* Add 3 bubbles here with k-number, product code, and "FDA Letter" which is a URL */}
                        <div className="prod-param-bubbles-container">
                            <div className="prod-param-bubble">
                                <div className="bubble-value">{productKNumber}</div>
                            </div>
                            <div className="prod-param-bubble">
                                <div className="bubble-value">{productCode || '---'}</div>
                            </div>
                            <div
                                className="prod-param-bubble-button"
                                role="button"
                                tabIndex={0}
                                onClick={() => window.open(fdaLetterUrl, "_blank", "noopener,noreferrer")}
                            >
                                <div className="bubble-value">FDA Letter</div>
                            </div>
                        </div>

                        {addYesMaybeNoButtons && (
                            <div className="predicate-candidate-yes-maybe-no-section">
                                <div className="tech-char-label">Predicate candidate?</div>

                                <div className="yes-maybe-no-button-group">
                                    <button
                                        type="button"
                            // className={localCandidate === "Yes" ? "yes-button" : "yes-button-inactive"}
                            className="yes-button"
                            onClick={() => {
                                setLocalCandidate("Yes");
                                if (typeof handleYes === "function") handleYes(productKNumber);
                            }}
                        >
                            Yes
                        </button>
                        <button
                            type="button"
                            // className={localCandidate === "Maybe" ? "maybe-button" : "maybe-button-inactive"}
                            className="maybe-button"
                            onClick={() => {
                                setLocalCandidate("Maybe");
                                if (typeof handleMaybe === "function") handleMaybe(productKNumber);
                            }}
                        >
                            Maybe
                        </button>
                        <button
                            type="button"
                            // className={localCandidate === "No" ? "no-button" : "no-button-inactive"}
                            className="no-button"
                            onClick={() => {
                                setLocalCandidate("No");
                                handleNo(productKNumber);
                            }}
                        >
                            No
                        </button>
                    </div>
                </div>
            )}

            {(() => {
                const leftFields = fields.filter(f => ["intendedUse", "indicationsForUse"].includes(f.name));
                const rightFields = fields.filter(f => !["intendedUse", "indicationsForUse", "deviceName", "productKNumber", "kNumber"].includes(f.name));

                const renderField = (field) => {
                    const {
                        name,
                        label,
                        value,
                        type = "text",
                        placeholder = "",
                        editable = false,
                        height
                    } = field;

                    const isTextArea = type === "textarea";
                    const isEditable = editable !== false;

                    const readOnlyStyle = {};
                    const readOnlyClampStyle = {};

                    if (!isEditable && height !== undefined) {
                        if (typeof height === "number") {
                            readOnlyClampStyle.display = "-webkit-box";
                            readOnlyClampStyle.WebkitBoxOrient = "vertical";
                            readOnlyClampStyle.WebkitLineClamp = height;
                            readOnlyClampStyle.overflow = "hidden";
                            readOnlyClampStyle.cursor = "pointer";
                        } else if (typeof height === "string") {
                            readOnlyStyle.height = height;
                            readOnlyStyle.overflow = "hidden";
                            readOnlyStyle.cursor = "pointer";
                        }
                    }

                    const truncated = !isEditable && isTruncatedMap[name];

                    return (
                        <div className="tech-char-label-group" key={name}>
                            <div className="tech-char-label">{label}</div>

                            {!isEditable ? (
                                <div
                                    id={name}
                                    ref={setRef(name)}
                                    className="tech-char-readonly"
                                    style={{ ...readOnlyStyle, ...readOnlyClampStyle }}
                                    onClick={() => {
                                        if (truncated) openPopup(label, value);
                                    }}
                                    title={truncated ? "Click to view full text" : undefined}
                                >
                                    {value}
                                </div>
                            ) : isTextArea ? (
                                <textarea
                                    id={name}
                                    className="tech-char-intended-use-textarea"
                                    placeholder={placeholder}
                                    value={value}
                                    onChange={(e) => handleChange(name, e.target.value)}
                                />
                            ) : (
                                <input
                                    id={name}
                                    className="tech-char-input"
                                    type={type}
                                    placeholder={placeholder}
                                    value={value}
                                    onChange={(e) => handleChange(name, e.target.value)}
                                />
                            )}
                        </div>
                    );
                };

                const expectedLeft = ["intendedUse", "indicationsForUse"];
                const defaultLabel = (n) => {
                    if (n === "intendedUse") return "Intended use";
                    if (n === "indicationsForUse") return "Indications for use";
                    return n;
                };

                const leftEntries = expectedLeft.map((name) => {
                    const f = fields.find((x) => x.name === name);
                    if (f) return f;
                    return { name, label: defaultLabel(name), value: "-", type: "text", editable: false };
                });

                return (
                    <div className="tech-char-two-column" style={{ display: "flex", gap: 24 }}>
                        <div className="left-column" style={{ flex: 1 }}>
                            {leftEntries.map(renderField)}
                        </div>
                        <div className="right-column" style={{ flex: 1 }}>
                            {rightFields.map(renderField)}
                        </div>
                    </div>
                );
            })()}

            {/* Add section separated by horizontal line with two lines: one for Predicates Used by This Product and one for Products Using This as Predicate */}
            <hr />
            <div className="predicate-section">
                <div className="predicates-used">
                    <div className="tech-char-label">Predicates Used by This Product</div>
                    <div className="tech-char-readonly">[List of predicates will go here]</div>
                </div>
                <div className="products-using">
                    <div className="tech-char-label">Products Using This as Predicate</div>
                    <div className="tech-char-readonly">[List of products will go here]</div>
                </div>
            </div>

            {popup.open && (
                <div
                    className="popup-overlay"
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                    }}
                    onClick={closePopup}
                >
                    <div
                        className="popup-box"
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#fff",
                            maxWidth: "40%",
                            maxHeight: "80%",
                            overflow: "auto",
                            padding: "16px",
                            borderRadius: "6px",
                            boxShadow: "0 4px 18px rgba(0,0,0,0.2)"
                        }}
                    >
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>{popup.label}</div>
                        <pre
                            style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                margin: 0,
                                fontFamily: "inherit"
                            }}
                        >
                            {popup.content}
                        </pre>

                        <div style={{ textAlign: "right", marginTop: 12 }}>
                            <button type="button" onClick={closePopup}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProductParametersPanel;